import { z } from 'zod';
import { ReviewDecisionSchema } from './review-decisions.js';
const Ref = z.string().min(1).max(100);
const Text = z.string().max(4000);
const NullableText = z.string().max(500).nullable();
// Presentation supplied by the purchasing model, never a new measurement grade.
export const InboxSummarySchema = z
    .object({
    headline: z.string().min(1).max(100),
    summary: z.string().min(1).max(220),
})
    .strict();
export const CoverageReasonSchema = z.enum([
    'assessment_missing',
    'configuration_changed',
    'measurements_changed',
    'freshness_unverified',
    'python_findings_unresolved',
    'scope_uncertain',
]);
export const EvidenceCitationSchema = z
    .object({
    kind: z.enum([
        'document',
        'source_snapshot',
        'observation',
        'manufacturer',
        'measurement_assessment',
    ]),
    id: z.string().min(1).max(2000),
    page: z.number().int().positive().nullable(),
    quote: z.string().max(1000),
})
    .strict();
const Evidence = z.array(EvidenceCitationSchema).max(40);
export const DocumentRoleSchema = z.enum([
    'measurement_form',
    'estimate',
    'signed_estimate',
    'change_order',
    'submitted_po',
    'draft_po',
    'acknowledgement',
    'manufacturer_confirmation',
    'approval',
    'other',
]);
export const OrderIdentitySchema = z
    .object({
    order_ref: Ref,
    po_number: NullableText,
    st_po_id: z
        .string()
        .regex(/^\d{1,24}$/)
        .nullable(),
    manufacturer: NullableText,
    manufacturer_order_number: NullableText,
    submitted_at: z.string().datetime({ offset: true }).nullable(),
    acknowledgement_received_at: z
        .string()
        .datetime({ offset: true })
        .nullable(),
    opening_refs: z.array(Ref).max(100),
    lines: z
        .array(z
        .object({
        line_ref: Ref,
        opening_refs: z.array(Ref).max(100),
        description: Text,
        quantity: NullableText,
        supply_scope: z.enum([
            'manufacturer',
            'separate_supply',
            'installation_only',
            'unknown',
        ]),
        source_refs: z.array(z.string().max(200)).max(40),
    })
        .strict())
        .max(500),
})
    .strict();
export const WorkflowManifestSchema = z
    .object({
    profile: z.literal('special_order'),
    orders: z.array(OrderIdentitySchema).max(100),
    openings: z
        .array(z
        .object({
        opening_ref: Ref,
        label: Text,
        source_refs: z.array(z.string().max(200)).max(40),
    })
        .strict())
        .max(100),
})
    .strict();
export const WorkflowDocumentSchema = z
    .object({
    role: DocumentRoleSchema,
    order_refs: z.array(Ref).max(100),
    opening_refs: z.array(Ref).max(100),
    revision_ref: NullableText,
    supersedes_document_ids: z.array(z.string().uuid()).max(100),
    approval: z
        .object({
        status: z.enum(['signed', 'approved', 'unapproved', 'unknown']),
        approved_at: z.string().datetime({ offset: true }).nullable(),
        approved_by: NullableText,
        evidence_refs: z.array(z.string().max(200)).max(40),
    })
        .strict(),
})
    .strict();
export const ComparisonSchema = z.enum([
    'po_vs_estimate',
    'ack_vs_po',
    'ack_vs_estimate',
]);
export const OutcomeSchema = z.enum([
    'match',
    'discrepancy',
    'unable_to_verify',
]);
export const ExceptionStateSchema = z.enum([
    'open',
    'awaiting_clarification',
    'recheck_required',
    'resolved',
]);
export const SpecificationSchema = z
    .object({
    order_ref: Ref,
    opening_ref: Ref.nullable(),
    line_ref: Ref.nullable(),
    field: Ref,
    document_id: z.string().uuid(),
    raw_value: NullableText,
    normalized_value: NullableText,
    unit: NullableText,
    presence: z.enum([
        'stated',
        'excluded',
        'missing',
        'unreadable',
        'not_applicable',
    ]),
    evidence: Evidence,
})
    .strict();
export const ComparisonCheckSchema = z
    .object({
    comparison: ComparisonSchema,
    opening_ref: Ref.nullable(),
    line_ref: Ref.nullable(),
    field: Ref,
    applicability: z.enum(['required', 'optional', 'not_applicable']),
    outcome: OutcomeSchema,
    expected_value: NullableText,
    actual_value: NullableText,
    explanation: Text,
    inbox: InboxSummarySchema.optional(),
    evidence: Evidence.min(1),
    policy_refs: z.array(Ref).max(30),
    severity: z.enum(['critical', 'high', 'normal', 'low']).nullable(),
    // Optional for stored results from earlier prompt versions. New reviews set both.
    attention: z
        .enum(['action_required', 'clarification', 'information'])
        .optional(),
    issue_kind: z
        .enum([
        'specification_conflict',
        'approval_gap',
        'missing_document',
        'code_ambiguity',
        'stage1_evidence',
        'routing_question',
        'cost_observation',
        'other',
    ])
        .optional(),
    // A historical omission stays unable_to_verify; later evidence can settle
    // the current configuration without rewriting the original comparison.
    resolved_by: z
        .object({
        comparison: z.literal('ack_vs_estimate'),
        explanation: Text.min(1),
        evidence: Evidence.min(1),
    })
        .strict()
        .nullable()
        .optional(),
    action: Text,
    responsible_role: z.enum(['PM', 'Design Pro']),
})
    .strict();
export const StageReviewSchema = z
    .object({
    stage: z.enum(['po', 'acknowledgement']),
    state: z.enum(['reviewed', 'awaiting_documents', 'not_applicable']),
    outcome: OutcomeSchema.nullable(),
    summary: Text,
    checks: z.array(ComparisonCheckSchema).max(500),
})
    .strict();
export const ReviewIssueCheckRefSchema = z
    .object({
    stage: z.enum(['po', 'acknowledgement']),
    comparison: ComparisonSchema,
    opening_ref: Ref.nullable(),
    line_ref: Ref.nullable(),
    field: Ref,
})
    .strict();
/** References preserve the independent comparison identity, including its stage. */
export function comparisonReferenceKey(value) {
    return JSON.stringify([
        value.stage,
        value.comparison,
        value.opening_ref,
        value.line_ref,
        value.field,
    ]);
}
/** Uses the model's disposition; never infers a purchasing conflict from values. */
export function isActionableComparison(check) {
    return (check.outcome !== 'match' &&
        check.attention !== 'information' &&
        check.applicability !== 'not_applicable');
}
export const ReviewIssueSchema = z
    .object({
    issue_ref: Ref,
    title: z.string().trim().min(1).max(120),
    explanation: z.string().trim().min(1).max(1500),
    next_action: z.string().trim().min(1).max(1000),
    check_refs: z.array(ReviewIssueCheckRefSchema).min(1).max(500),
    evidence: Evidence.min(1),
    document_context: z
        .array(z
        .object({
        document_id: z.string().uuid(),
        role: z.enum([
            'approved_purchase',
            'revision_approval_unverified',
            'submitted_po',
            'acknowledgement',
            'supporting',
        ]),
        summary: z.string().trim().min(1).max(600),
    })
        .strict())
        .max(20),
})
    .strict();
export const MeasurementApplicabilitySchema = z
    .object({
    state: z.enum([
        'covered',
        'fresh_stage1_review_required',
        'assessment_unavailable',
        'not_applicable',
    ]),
    assessment_ids: z.array(z.string().uuid()).max(100),
    explanation: Text,
    inbox: InboxSummarySchema.optional(),
    reason_codes: z.array(CoverageReasonSchema).max(6).optional(),
    unresolved_python_exceptions: z.array(Text).max(100),
    evidence: Evidence,
})
    .strict();
export const OrderReviewSchema = z
    .object({
    identity: OrderIdentitySchema,
    measurement: MeasurementApplicabilitySchema,
    verification: z.enum(['verified', 'not_verified']),
    stages: z.array(StageReviewSchema).length(2),
    // Earlier stored reviews retain their field-based presentation.
    review_issues: z.array(ReviewIssueSchema).max(500).optional(),
})
    .strict();
export const WorkflowResultSchema = z
    .object({
    schema_version: z.literal('2'),
    orders: z.array(OrderReviewSchema).max(100),
    specifications: z.array(SpecificationSchema).max(3000),
    collection_requests: z
        .array(z
        .object({
        order_ref: Ref.nullable(),
        document_role: DocumentRoleSchema,
        reason: Text,
        timing: z.enum(['needed_now', 'expected_later']),
    })
        .strict())
        .max(300),
    unassigned_document_ids: z.array(z.string().uuid()).max(100),
})
    .strict();
// Live generation must supply groups (an empty array is correct for quiet success).
export const CurrentWorkflowResultSchema = WorkflowResultSchema.extend({
    orders: z
        .array(OrderReviewSchema.extend({
        review_issues: z.array(ReviewIssueSchema).max(500),
    }))
        .max(100),
});
const Hours = z.number().int().min(1).max(8760).nullable();
export const WorkflowPolicySchema = z
    .object({
    approved_rules: z.string().max(40000),
    acknowledgement_due_hours: Hours,
    response_due_hours: Hours,
    escalation_after_hours: Hours,
    collection_interval_hours: Hours,
    review_timeout_hours: Hours,
})
    .strict();
export const DEFAULT_WORKFLOW_POLICY = {
    approved_rules: '',
    acknowledgement_due_hours: null,
    response_due_hours: null,
    escalation_after_hours: null,
    collection_interval_hours: null,
    review_timeout_hours: null,
};
/** Validated model disposition; does not infer equivalence from document values. */
export function hasEvidenceResolution(content) {
    const check = ComparisonCheckSchema.safeParse(content);
    return Boolean(check.success &&
        check.data.outcome === 'unable_to_verify' &&
        check.data.attention === 'information' &&
        check.data.resolved_by);
}
export const ResolutionEvidenceSchema = z
    .array(z
    .object({
    kind: z.enum(['document', 'measurement_assessment']),
    id: z.string().uuid(),
})
    .strict())
    .max(50);
export const ExceptionMutationSchema = z
    .object({
    action: z.enum([
        'acknowledge',
        'assign',
        'await_clarification',
        'recheck',
        'resolve',
        'reopen',
    ]),
    expected_updated_at: z.string().datetime({ offset: true }),
    note: z.string().trim().min(1).max(4000),
    assigned_user_id: z.string().uuid().nullable().optional(),
    run_id: z.string().uuid().optional(),
    evidence: ResolutionEvidenceSchema.default([]),
})
    .strict();
export const CollectionCheckSchema = z
    .object({
    base_submission_id: z.string().uuid(),
    source_run_id: z.string().uuid().nullable(),
    outcome: z.enum(['unchanged', 'not_found', 'retrieval_failed']),
    note: z.string().trim().min(1).max(2000),
    next_check_at: z.string().datetime({ offset: true }),
})
    .strict();
const Json = z.record(z.string(), z.unknown());
export const WorkflowExceptionSchema = z.object({
    review_decision: ReviewDecisionSchema.nullable().default(null),
    review_source_version: z.string().default(''),
    last_decision_id: z.string().nullable().default(null),
    review_status: z.string().default('needs_review'),
    id: z.string(),
    order_id: z.string().nullable(),
    stage: z.string(),
    field: z.string(),
    opening_ref: z.string().nullable(),
    line_ref: z.string().nullable(),
    state: ExceptionStateSchema,
    severity: z.string(),
    responsible_role: z.string(),
    assigned_user_id: z.string().nullable(),
    content: Json,
    latest_outcome: z.string(),
    latest_run_id: z.string().nullable(),
    resolved_run_id: z.string().nullable(),
    resolution_evidence: ResolutionEvidenceSchema,
    recheck_evidence: z.array(EvidenceCitationSchema),
    due_at: z.string().nullable(),
    acknowledged_at: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
});
export const WorkflowPolicyViewSchema = z.object({
    version: z.number(),
    settings: WorkflowPolicySchema,
    updated_at: z.string().nullable(),
});
export const WorkflowDetailSchema = z.object({
    assessments: z.array(z.object({
        id: z.string(),
        source_review_id: z.string(),
        source_fingerprint: z.string(),
        provenance: Json,
        assessment: Json,
        imported_at: z.string(),
        current: z.boolean(),
    })),
    orders: z.array(z.object({
        review_completion: z
            .object({
            id: z.string(),
            action: z.enum(['complete', 'reopen']),
            note: z.string(),
            created_at: z.string(),
            actor_name: z.string().nullable(),
        })
            .nullable()
            .default(null),
        id: z.string(),
        order_ref: z.string(),
        identity: OrderIdentitySchema,
        inventory_submission_id: z.string(),
        hold_status: z.string(),
        updated_at: z.string(),
        result: OrderReviewSchema.nullable(),
        run_id: z.string().nullable(),
        submission_id: z.string().nullable(),
    })),
    exceptions: z.array(WorkflowExceptionSchema),
    events: z.array(z.object({
        id: z.string(),
        action: z.string(),
        actor_user_id: z.string().nullable(),
        exception_id: z.string().nullable(),
        details: Json,
        created_at: z.string(),
    })),
    collection_work: z.array(z.object({
        id: z.string(),
        order_ref: z.string().nullable(),
        document_role: z.string(),
        reason: z.string(),
        next_check_at: z.string(),
        required_by: z.string().nullable(),
        last_outcome: z.string().nullable(),
    })),
    members: z.array(z.object({ user_id: z.string(), name: z.string(), email: z.string() })),
    policy: WorkflowPolicyViewSchema,
});
export const WorkflowInboxSchema = z.object({
    items: z.array(z.object({
        id: z.string(),
        project_id: z.string(),
        st_project_id: z.string(),
        display_number: z.string(),
        order_ref: z.string().nullable(),
        kind: z.enum(['exception', 'review_incomplete', 'overdue_document']),
        stage: z.string(),
        field: z.string(),
        opening_ref: z.string().nullable(),
        line_ref: z.string().nullable(),
        state: ExceptionStateSchema,
        severity: z.string(),
        responsible_role: z.string(),
        assigned_user_id: z.string().nullable(),
        content: Json,
        latest_outcome: z.string(),
        due_at: z.string().nullable(),
        created_at: z.string(),
        alert_count: z.number().int().positive(),
    })),
    total: z.number(),
    policy: WorkflowPolicyViewSchema,
});
